import { LuSearch, LuBell } from 'react-icons/lu'
import { useLocation } from 'react-router-dom'
import { useUIStore } from '@stores/index'
import { cn } from '@lib/cn'
import { ThemeToggle } from '@components/ui/ThemeToggle'
import { CountryFilter } from '@/features/dashboard/components/CountryFilter'
import { useMapStore } from '@stores/mapStore'
import { InfoTooltip } from '@components/ui/InfoTooltip'

export function Header() {
  const { sidebarCollapsed, globalSearchQuery, setGlobalSearchQuery } = useUIStore()
  const { flyTo, setSearchMarker } = useMapStore()
  const location = useLocation()

  // Show CountryFilter on dashboard and portfolio pages (same position)
  // Also enable coordinate search on portfolio detail pages (/portfolio/:id)
  const showCountryFilter = location.pathname === '/dashboard' || location.pathname === '/portfolio'
  const showCoordinateSearch = showCountryFilter || location.pathname.startsWith('/portfolio/')

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && showCoordinateSearch) {
      // Try to parse "Lat, Lng" format
      // Allow spaces, optional decimals
      const pattern = /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/
      const match = globalSearchQuery.trim().match(pattern)

      if (match) {
        const lat = parseFloat(match[1])
        const lng = parseFloat(match[2])

        // Simple validation
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          setSearchMarker({ latitude: lat, longitude: lng })
          flyTo(lng, lat, 10)
          // Clear search query to prevent filtering portfolios by coordinates
          setGlobalSearchQuery('')
        }
      }
    }
  }

  return (
    <header
      className={cn(
        'fixed top-0 right-0 h-16 bg-[var(--glass-bg)] backdrop-blur-sm border-b border-[var(--color-border)] z-40',
        'flex items-center justify-between px-6',
        'left-0', // Default mobile
        sidebarCollapsed ? 'md:left-20' : 'md:left-60',

        'transition-all duration-300'
      )}
    >
      {/* Left Section: Search + Country Filter */}
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative flex items-center gap-2">
          <div className="relative">
            <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
            <input
              type="text"
              placeholder={showCoordinateSearch ? "Search or Lat, Lng..." : "Search..."}
              aria-label="Search"
              value={globalSearchQuery}
              onChange={(e) => setGlobalSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-64 pl-10 pr-4 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-colors"
            />
          </div>
          {showCoordinateSearch && (
            <InfoTooltip
              content="Search by coordinates: Enter 'Latitude, Longitude' (e.g., 14.5995, 120.9842) and press Enter to place a marker."
              position="right"
              className="text-[var(--color-text-muted)] hover:text-sky-500"
            />
          )}
        </div>

        {/* Country Filter - only shown on dashboard page */}
        {showCountryFilter && <CountryFilter />}
      </div>

      {/* Right Section: Actions */}
      <div className="flex items-center gap-2">
        <ThemeToggle />
        <button
          className="p-2 hover:bg-[var(--color-surface)] rounded-lg relative transition-colors"
          aria-label="Notifications"
        >
          <LuBell className="w-5 h-5 text-[var(--color-text-muted)]" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        </button>
      </div>
    </header>
  )
}
